import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAccess } from '../contexts/AccessContext';
import { Clock } from 'lucide-react';

/**
 * Compact trial countdown pill shown in the top nav.
 *
 * Visibility rules:
 *  - Only rendered for users whose `access.access_type === "trial"`
 *  - Hidden while access is still loading (no UI flicker)
 *  - Hidden when the trial has already expired (the ExpiredAccessGuard
 *    wrapper redirects those users to /pricing anyway)
 *
 * Behaviour:
 *  - Ticks every 60s — shows "Xh YYm" while > 1h remains, "YYm" otherwise
 *  - Color escalates: indigo (>6h) → amber (1-6h) → red (<1h) for urgency
 *  - Click → /pricing
 */
const TrialCountdownPill = () => {
  const { access, loading } = useAccess();
  const navigate = useNavigate();
  const [now, setNow] = useState(() => Date.now());

  // Tick every minute so the countdown updates without reload
  useEffect(() => {
    if (access?.access_type !== 'trial') return;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [access?.access_type]);

  const timeInfo = useMemo(() => {
    if (!access?.expires_at) return null;
    const expiresMs = new Date(access.expires_at).getTime();
    if (Number.isNaN(expiresMs)) return null;
    const msLeft = expiresMs - now;
    if (msLeft <= 0) return { expired: true };
    const hoursLeft = msLeft / (1000 * 60 * 60);
    const h = Math.floor(hoursLeft);
    const m = Math.floor((msLeft % (1000 * 60 * 60)) / (1000 * 60));
    return { hoursLeft, h, m, expired: false };
  }, [access?.expires_at, now]);

  if (loading) return null;
  if (access?.access_type !== 'trial') return null;
  if (!timeInfo || timeInfo.expired) return null;

  const { hoursLeft, h, m } = timeInfo;
  // Color escalation based on urgency (scaled for 48h trial:
  // >12h calm, 2-12h warning, <2h critical).
  const tone =
    hoursLeft > 12
      ? 'bg-indigo-500/15 text-indigo-200 border-indigo-400/30 hover:bg-indigo-500/25'
      : hoursLeft > 2
      ? 'bg-amber-500/15 text-amber-200 border-amber-400/30 hover:bg-amber-500/25'
      : 'bg-red-500/20 text-red-100 border-red-400/40 hover:bg-red-500/30 animate-pulse';

  const label = h > 0 ? `${h}h ${m}m` : `${m}m`;

  return (
    <button
      onClick={() => navigate('/pricing')}
      className={`hidden md:inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-full border transition-colors ${tone}`}
      data-testid="trial-countdown-pill"
      title="Πάτα για αναβάθμιση σε πακέτο"
    >
      <Clock className="w-3.5 h-3.5" />
      <span className="whitespace-nowrap">
        Trial: {label}
      </span>
      <span className="hidden lg:inline text-white/70 font-normal">· Αναβάθμιση</span>
    </button>
  );
};

export default TrialCountdownPill;
